import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import languange from "../../assets/icons/language.svg";
import menu from "../../assets/icons/dehaze.svg";
import { Link } from "react-router-dom";
import build from "../../assets/icons/build.svg";
import user from "../../assets/icons/user.svg";
import article from "../../assets/icons/article-white.svg";
import key from "../../assets/icons/key.svg";
import logout from "../../assets/icons/logout.svg";
import { MdArrowForwardIos } from "react-icons/md";
import AnimatedPopup from "./AnimatedPopup";

export default function NavigationBar() {
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("KR");

  const languageDropdownRef = useRef(null);
  const profileDropdownRef = useRef(null);

  const languages = [
    { code: "KR", name: "한국어" },
    { code: "EN", name: "English" },
  ];

  const menuItems = [
    { label: "프로젝트 관리", link: "/project-management", icon: build },
    { label: "사용자 관리", link: "/profile", icon: user },
    { label: "프로젝트 리스트", link: "/projects", icon: article },
  ];

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        languageDropdownRef.current &&
        !languageDropdownRef.current.contains(event.target)
      ) {
        setIsLanguageOpen(false);
      }
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target)
      ) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const dropdownVariants = {
    hidden: {
      opacity: 0,
      scale: 0.95,
      y: -10,
      transition: { duration: 0.2, ease: "easeOut" },
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { duration: 0.2, ease: "easeOut" },
    },
  };

  const itemVariants = {
    hidden: { x: -10, opacity: 0 },
    visible: (i) => ({
      x: 0,
      opacity: 1,
      transition: { delay: i * 0.05, duration: 0.2 },
    }),
  };

  // change password popup
  const [passwordChangePopupOpen, setPasswordChangePopupOpen] = useState(false);

  return (
    <div className="flex items-center sm:gap-5 gap-3">
      {/* 🌐 Language Selector */}
      <div className="relative" ref={languageDropdownRef}>
        <button
          onClick={() => {
            setIsLanguageOpen(!isLanguageOpen);
            setIsProfileOpen(false);
          }}
          className="flex items-center gap-1.5"
        >
          <img src={languange} alt="Language" className="w-6 h-6" />
          <span className="text-[#8E98C7] text-[16px] font-normal roboto-font">
            {selectedLanguage}
          </span>
        </button>

        {/* Dropdown */}
        <AnimatePresence>
          {isLanguageOpen && (
            <motion.div
              variants={dropdownVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              className="absolute top-full right-0 mt-2 w-[100px] bg-white rounded-[10px] shadow-[0_9px_12px_0_rgba(0,_0,_0,_0.15)] overflow-hidden z-50"
            >
              <ul className="flex flex-col py-2">
                {languages.map((lang, i) => (
                  <motion.li
                    key={lang.code}
                    custom={i}
                    variants={itemVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    <button
                      onClick={() => {
                        setSelectedLanguage(lang.code);
                        setIsLanguageOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-[14px] transition-colors duration-200 ${
                        selectedLanguage === lang.code
                          ? "bg-[#EDF0FB] text-[#08F]"
                          : "text-[#242424] hover:bg-[#EDF0FB]"
                      }`}
                    >
                      {lang.name}
                    </button>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* 👤 Profile Dropdown */}
      <div className="relative h-6 w-6" ref={profileDropdownRef}>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            setIsProfileOpen(!isProfileOpen);
            setIsLanguageOpen(false);
          }}
        >
          <div className="flex items-center justify-center">
            <img
              src={menu}
              alt="Profile"
              className="w-full h-full object-cover"
            />
          </div>
        </motion.button>

        <AnimatePresence>
          {isProfileOpen && (
            <motion.div
              variants={dropdownVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              className="absolute top-full right-0 py-[14px] mt-2 w-[190px] bg-white text-[#242424] rounded-[10px] shadow-[0_9px_12px_0_rgba(0,_0,_0,_0.15)] overflow-hidden z-50"
            >
              {/* Menu Items */}
              <ul className="w-full">
                {menuItems.map((item, index) => (
                  <motion.li
                    key={item.label}
                    custom={index}
                    variants={itemVariants}
                    initial="hidden"
                    animate="visible"
                    onClick={item.action}
                    className="w-full"
                  >
                    <Link
                      to={item.link}
                      onClick={() => setIsProfileOpen(false)}
                      className="w-full text-left pl-5 pr-2.5 py-1.5 transition-colors duration-200 text-[#242424] hover:text-black text-[12px] font-[300] hover:bg-[#EDF0FB] flex items-center justify-between gap-2.5"
                    >
                      <img src={item.icon} alt="" />
                      <span className="flex-1">{item.label}</span>
                      <MdArrowForwardIos color="#242424" />
                    </Link>
                  </motion.li>
                ))}

                {/* change password */}
                <motion.li
                  custom={4}
                  variants={itemVariants}
                  initial="hidden"
                  animate="visible"
                  className="w-full"
                >
                  <button
                    onClick={() => {
                      setPasswordChangePopupOpen(true);
                      setIsProfileOpen(false);
                    }}
                    className="w-full text-left pl-5 pr-2.5 py-1.5 transition-colors duration-200 text-[#242424] hover:text-black text-[12px] font-[300] hover:bg-[#EDF0FB] flex items-center justify-between gap-2.5"
                  >
                    <img src={key} alt="" />
                    <span className="flex-1">비밀번호 변경</span>
                    <MdArrowForwardIos color="#242424" />
                  </button>
                </motion.li>

                {/* logout */}
                <motion.li
                  custom={5}
                  variants={itemVariants}
                  initial="hidden"
                  animate="visible"
                  className="w-full"
                >
                  <button
                    onClick={() => setIsProfileOpen(false)}
                    className="w-full text-left pl-5 pr-2.5 py-1.5 transition-colors duration-200 text-[#242424] hover:text-black text-[12px] font-[300] hover:bg-[#EDF0FB] flex items-center justify-between gap-2.5"
                  >
                    <img src={logout} alt="" />
                    <span className="flex-1">로그아웃</span>
                    <MdArrowForwardIos color="#242424" />
                  </button>
                </motion.li>
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* 🔒 Change password popup */}
      <AnimatedPopup
        label="비밀번호 변경"
        isOpen={passwordChangePopupOpen}
        onClose={() => setPasswordChangePopupOpen(false)}
        className="max-w-[573px]"
      >
        <form action="" className="flex flex-col gap-4 sm:gap-5 sm:p-10 p-6">
          <InputItem
            label="현재 비밀번호"
            placeholder="현재 비밀번호를 입력해 주세요."
          />
          <InputItem
            label="새로운 비밀번호"
            placeholder="새로운 비밀번호를 입력해 주세요."
          />
          <InputItem
            label="새로운 비밀번호 확인"
            placeholder="새로운 비밀번호를 입력해 주세요."
          />

          <button className="mt-2 text-white text-[16px] py-2 px-4 font-normal max-w-[160px] w-full ml-auto rounded-[10px] bg-[#08F] hover:bg-transparent hover:text-[#08F] border border-[#08F] transition-all text-center duration-300">
            저장
          </button>
        </form>
      </AnimatedPopup>
    </div>
  );
}

const InputItem = ({ label, placeholder }) => {
  return (
    <div className="grid sm:grid-cols-[153px_1fr] items-center gap-2 sm:gap-5">
      <p className="text-[#242424] sm:text-[16px] text-[14px] font-normal">
        {label}
      </p>
      <input
        type="password"
        className="border border-[#BABABA] rounded-[6px] sm:rounded-[10px] flex-1 w-full bg-transparent py-1.5 sm:py-2 px-4 placeholder:text-center outline-none sm:text-[16px] text-[14px] font-normal text-[#242424] placeholder:text-[#C9C9C9]"
        placeholder={placeholder}
        required
      />
    </div>
  );
};
